import { createSlice } from '@reduxjs/toolkit'
import {
  createUserDeliveryAddressAction,
  fetchUserFacebookAction,
  fetchUserViaOddlePassAction,
  getAllAddressesOPAction,
  getAllSavedCardsAction,
  logoutAction,
  removeUserDeliveryAddressAction,
  setDefaultUserDeliveryAddressAction,
  updateUserAction,
  updateUserDeliveryAddressAction
} from './action'
import { isEmpty } from 'src/utils/string'
import { HYDRATE } from 'next-redux-wrapper'

type Nullable<T> = T | null

type PlainInfo<T> = {
  status: RequestState
  error?: string
  data: Nullable<T>
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

interface UserState {
  user: PlainInfo<UserStateData>
  savedCards: PlainInfo<UserSavedCreditCard>
}

const initialState: UserState = {
  user: {
    status: 'none',
    data: {} as UserStateData,
    error: null
  },
  savedCards: {
    status: 'none',
    data: {} as UserSavedCreditCard,
    error: null
  }
}

export type UserStateInReducer = {
  '@user': UserState
}

export const userSlice = createSlice({
  name: '@user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserFacebookAction.pending, (state) => {
        state.user.status = 'pending'
      })
      .addCase(fetchUserFacebookAction.fulfilled, (state, action) => {
        state.user.status = 'fulfilled'
        const { ocaExist, mcaExist } = action.payload.payload
        if (ocaExist && mcaExist) {
          state.user.data = action.payload.payload
        }
      })
      .addCase(fetchUserFacebookAction.rejected, (state, action) => {
        state.user.status = 'rejected'
        state.user.error = action.error.message
      })
      .addCase(logoutAction.fulfilled, (state) => {
        state.user.data = {} as UserStateData
      })
      .addCase(setDefaultUserDeliveryAddressAction.pending, (state, action) => {
        state.user.data.preferredDeliveryAddress = {
          id: action[1]
        } as PreferredDeliveryAddress
      })
      .addCase(
        setDefaultUserDeliveryAddressAction.fulfilled,
        (state, action) => {
          if (action.payload.success && action.payload.data) {
            state.user.data.preferredDeliveryAddress =
              state.user.data.deliveryAddresses.find(
                (address) =>
                  address.id === action.payload.data.preferredDeliveryAddressId
              ) as unknown as PreferredDeliveryAddress
          } else {
            // this handle for response from OMS server
            state.user.data.preferredDeliveryAddress = action.payload.payload
          }
        }
      )
      .addCase(createUserDeliveryAddressAction.fulfilled, (state, action) => {
        if (!state.user.data.deliveryAddresses) {
          state.user.data.deliveryAddresses = []
        }
        if (action.payload.success && action.payload.data) {
          state.user.data.deliveryAddresses.push(action.payload.data)
        } else if (isEmpty(action.payload?.payload?.error)) {
          state.user.data.deliveryAddresses.push(action.payload)
        }
      })
      .addCase(updateUserDeliveryAddressAction.fulfilled, (state, action) => {
        let index = 0
        const payload =
          action.payload.payload.success && action.payload.payload.data
            ? action.payload.payload.data
            : action.payload.payload
        state.user.data.deliveryAddresses.find((a, i) => {
          if (a.id === payload.id) index = i
        })
        state.user.data.deliveryAddresses[index] = payload
      })
      .addCase(removeUserDeliveryAddressAction.fulfilled, (state, action) => {
        state.user.data.deliveryAddresses =
          state.user.data.deliveryAddresses.filter(
            (place) => place.id !== action.payload.payload?.deletedAddressId
          )
        if (
          state.user.data.preferredDeliveryAddress &&
          state.user.data.preferredDeliveryAddress.id ===
            action.payload.payload?.deletedAddressId
        ) {
          state.user.data.preferredDeliveryAddress = null
        }
      })
      .addCase(removeUserDeliveryAddressAction.rejected, (state, action) => {
        state.user.error = action.error.message
      })
      .addCase(getAllAddressesOPAction.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.user.data.deliveryAddresses = action.payload.data
          const preferredDeliveryAddress = action.payload.data.find(
            (address) => address.preferredDeliveryAddress
          )
          if (preferredDeliveryAddress) {
            state.user.data.preferredDeliveryAddress = preferredDeliveryAddress
          }
        }
      })
      .addCase(getAllAddressesOPAction.rejected, (state, action) => {
        state.user.data.fetchedAddressWithError = action.error.message
      })
      .addCase(getAllSavedCardsAction.pending, (state) => {
        state.savedCards.status = 'pending'
      })
      .addCase(getAllSavedCardsAction.fulfilled, (state, action) => {
        state.savedCards.status = 'fulfilled'
        state.savedCards.data = action.payload
      })
      .addCase(getAllSavedCardsAction.rejected, (state, action) => {
        state.savedCards.status = 'rejected'
        state.savedCards.error = action.error.message
      })
      .addCase(fetchUserViaOddlePassAction.fulfilled, (state, action) => {
        state.user.status = 'fulfilled'
        if (action.payload.payload.error) {
          state.user.status = 'rejected'
          state.user.error = action.payload.payload.error
        }
        state.user.data = action.payload.payload
      })
      .addCase(updateUserAction.fulfilled, (state, action) => {
        state.user.status = 'fulfilled'
        if (!action.payload.payload.error) {
          state.user.data = action.payload.payload
        }
      })
      .addCase(HYDRATE, (state, action) => ({
        ...state,
        ...action['payload']['@user']
      }))
      .addMatcher(
        (action) => {
          const sameHandler = [
            '@user/fetchUser/fulfilled',
            '@user/loginAction/fulfilled',
            '@user/signUpAction/fulfilled'
          ]
          if (sameHandler.includes(action.type)) {
            return true
          }
        },
        (state, action) => {
          state.user.status = 'fulfilled'
          if (action.payload.payload.error) {
            state.user.status = 'rejected'
          }
          state.user.data = action.payload.payload
          state.user.error = action.payload.payload.error
        }
      )
  }
})
